import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectApi, photoApi, documentApi, activityApi, teamApi, subcontractorApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ProjectNotes } from '../components/project/ProjectNotes';
import { ProjectChangeOrders } from '../components/project/ProjectChangeOrders';
import LeadFiles from '../components/LeadFiles';
import EstimateSection from '../components/EstimateSection';
import ProjectCosts from '../components/ProjectCosts';
import LaborVsEstimate from '../components/LaborVsEstimate';
import SimpleTaskList from '../components/SimpleTaskList';
import PhaseTasks from '../components/PhaseTasks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  MapPin,
  DollarSign,
  Calendar,
  Pencil,
  Trash2,
  Share2,
  Check,
  Camera,
  Plus,
  Phone,
  Mail,
  Image,
  FileText,
  Download,
  ListTodo,
  User,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';

const PROJECT_PHASES = ['Design', 'Permit', 'Demo', 'Framing', 'Rough-ins', 'Drywall', 'Finishes', 'Final Inspection'];
const DOCUMENT_TYPES = ['contract', 'permit', 'invoice', 'other'];

const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

// Like formatCurrency but always renders an amount (including $0)
const money = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
}).format(value || 0);

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [project, setProject] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assignedManager, setAssignedManager] = useState(null);
  const [billingSummary, setBillingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoPhase, setPhotoPhase] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [docName, setDocName] = useState('');
  const [docType, setDocType] = useState('contract');
  const [activeTab, setActiveTab] = useState('phases');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (isOwner) {
      fetchManagers();
    }
  }, [isOwner]);

  const fetchData = async () => {
    try {
      const [projectRes, photosRes, docsRes] = await Promise.all([
        projectApi.getOne(id),
        photoApi.getAll(id),
        documentApi.getAll(id),
      ]);
      setProject(projectRes.data);
      setPhotos(photosRes.data);
      setDocuments(docsRes.data);

      // Build combined assignee list (team members + subcontractors) for task assignment
      try {
        const [teamRes, subsRes] = await Promise.all([teamApi.getMembers(), subcontractorApi.getAll()]);
        const team = (teamRes.data || []).map((m) => ({ id: m.id, name: m.name, type: 'user' }));
        const subs = (subsRes.data || []).map((s) => ({ id: s.id, name: s.name || s.company_name || 'Subcontractor', type: 'subcontractor' }));
        setAssignees([...team, ...subs]);
      } catch (e) { /* non-blocking */ }
      
      // Fetch assigned manager info
      if (projectRes.data.assigned_manager_id) {
        try {
          const managerRes = await projectApi.getAssignedManager(id);
          setAssignedManager(managerRes.data.assigned_manager);
        } catch (e) {
          // Manager details are optional; ignore fetch failure
          console.debug('Could not load assigned manager details', e);
        }
      }

      // Billing progress (only available to users who can view financials)
      try {
        const billingRes = await projectApi.getBillingSummary(id);
        setBillingSummary(billingRes.data);
      } catch (e) {
        setBillingSummary(null);
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await teamApi.getMembers();
      // Assignable users = owner + managers (owner first)
      const assignable = response.data.filter(m => m.role === 'owner' || m.role === 'manager');
      assignable.sort((a, b) => (a.role === 'owner' ? -1 : b.role === 'owner' ? 1 : 0));
      setManagers(assignable);
    } catch (error) {
      console.log('Could not fetch managers');
    }
  };

  const handleAssignManager = async (managerId) => {
    try {
      await projectApi.assign(id, managerId || null);
      const manager = managers.find(m => m.id === managerId);
      setProject({ ...project, assigned_manager_id: managerId || null });
      setAssignedManager(manager || null);
      toast.success(managerId ? `Project assigned to ${manager?.name}` : 'Project unassigned');
    } catch (error) {
      toast.error('Failed to assign project');
    }
  };

  const handlePhaseToggle = async (phaseName, completed) => {
    try {
      const response = await projectApi.updatePhase(id, {
        phase: phaseName,
        completed,
      });
      
      const updatedPhases = project.phases.map((p) =>
        p.name === phaseName ? { ...p, completed, completed_at: completed ? new Date().toISOString() : null } : p
      );
      setProject({
        ...project,
        phases: updatedPhases,
        current_phase: response.data.current_phase,
      });
      
      toast.success(`${phaseName} ${completed ? 'completed' : 'reopened'}`);
    } catch (error) {
      toast.error('Failed to update phase');
    }
  };



  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingPhoto(true);
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const response = await photoApi.upload({
          project_id: id,
          caption: photoCaption,
          phase: photoPhase || project.current_phase,
          image_data: reader.result,
        });
        setPhotos([response.data, ...photos]);
        setPhotoCaption('');
        toast.success('Photo uploaded');
      } catch (error) {
        toast.error('Failed to upload photo');
      } finally {
        setUploadingPhoto(false);
      }
    };
    
    reader.readAsDataURL(file);
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!docName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setUploadingDoc(true);
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const fileExt = file.name.split('.').pop().toLowerCase();
        const response = await documentApi.upload({
          project_id: id,
          name: docName,
          document_type: docType,
          file_data: reader.result,
          file_type: fileExt,
        });
        setDocuments([response.data, ...documents]);
        setDocName('');
        toast.success('Document uploaded');
      } catch (error) {
        toast.error('Failed to upload document');
      } finally {
        setUploadingDoc(false);
      }
    };
    
    reader.readAsDataURL(file);
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await photoApi.delete(photoId);
      setPhotos(photos.filter((p) => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentApi.delete(docId);
      setDocuments(documents.filter((d) => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await projectApi.delete(id);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to delete project');
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/portal/${project.share_token}`;
    
    try {
      // Try the modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Client portal link copied!');
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (err) {
      // Fallback: Create a temporary input element
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success('Client portal link copied!');
        } else {
          // If copy still fails, show the link in a toast so user can copy manually
          toast.info(`Share link: ${url}`, { duration: 10000 });
        }
      } catch (fallbackErr) {
        // Last resort: show the link
        toast.info(`Share link: ${url}`, { duration: 10000 });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (!project) return null;

  const isSmall = project.project_size === 'small';
  const taskList = project.tasks || [];
  const completedTasks = taskList.filter((t) => t.completed).length;
  const phaseCount = project.phases?.length || 0;
  // Standard projects: overall progress = % of all phase tasks done (sub-tasks count toward their task)
  const allPhaseTasks = (project.phases || []).flatMap((p) => p.tasks || []);
  const tfrac = (t) => {
    if (t.completed) return 1;
    const s = t.subtasks || [];
    return s.length ? s.filter((x) => x.completed).length / s.length : 0;
  };
  const progress = isSmall
    ? (taskList.length > 0 ? Math.round((completedTasks / taskList.length) * 100) : 0)
    : (allPhaseTasks.length > 0
        ? Math.round((allPhaseTasks.reduce((a, t) => a + tfrac(t), 0) / allPhaseTasks.length) * 100)
        : (phaseCount > 0
            ? Math.round((project.phases.filter((p) => p.completed).length / phaseCount) * 100)
            : 0));

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/projects')}
            className="rounded-sm"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{project.name}</h1>
            <p className="text-muted-foreground">{project.client_name}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-sm text-sm"
            onClick={copyShareLink}
            data-testid="share-btn"
          >
            <Share2 className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Share with Client</span>
            <span className="sm:hidden">Share</span>
          </Button>
          <Button
            variant="outline"
            className="rounded-sm text-sm"
            onClick={() => navigate(`/projects/${id}/edit`)}
            data-testid="edit-project-btn"
          >
            <Pencil className="h-4 w-4 mr-1 sm:mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-project-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Progress Card */}
          <Card className="rounded-sm border-t-4 border-t-orange-500">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Project Progress</CardTitle>
                <Badge
                  className={
                    project.status === 'active'
                      ? 'bg-green-700'
                      : project.status === 'completed'
                      ? 'bg-slate-900'
                      : ''
                  }
                >
                  {project.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span>
                    {isSmall
                      ? <>Tasks: <strong>{completedTasks}/{taskList.length} complete</strong></>
                      : <>Current Phase: <strong>{project.current_phase}</strong></>}
                  </span>
                  <span className="font-semibold">{progress}% Complete</span>
                </div>
                <Progress value={progress} className="h-3" />
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="phases">
                {project.project_size === 'small' ? 'Tasks' : 'Phases & Tasks'}
              </TabsTrigger>
              <TabsTrigger value="costs" data-testid="project-costs-tab">Costs</TabsTrigger>
              <TabsTrigger value="files" data-testid="project-files-tab">Files</TabsTrigger>
              <TabsTrigger value="estimates" data-testid="project-estimates-tab">Estimates</TabsTrigger>
              <TabsTrigger value="change-orders" data-testid="project-change-orders-tab">Change Orders</TabsTrigger>
              <TabsTrigger value="notes" data-testid="project-notes-tab">Notes</TabsTrigger>
            </TabsList>

            {/* Phases/Tasks Tab */}
            <TabsContent value="phases">
              {project.project_size === 'small' ? (
                <SimpleTaskList
                  projectId={id}
                  tasks={project.tasks || []}
                  onUpdate={fetchData}
                />
              ) : (
              <Card className="rounded-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ListTodo className="h-5 w-5" />
                    Phase Tracker with Tasks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {project.phases.map((phase, index) => (
                      <div
                        key={phase.name}
                        className={`border rounded-sm p-4 ${
                          phase.completed ? 'bg-green-50 border-green-200' : phase.name === project.current_phase ? 'bg-orange-50 border-orange-200' : ''
                        }`}
                        data-testid={`phase-${phase.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={phase.completed}
                            onCheckedChange={(checked) => handlePhaseToggle(phase.name, checked)}
                            className="h-5 w-5"
                            data-testid={`phase-checkbox-${index}`}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{phase.name}</p>
                            {phase.completed && phase.completed_at && (
                              <p className="text-xs text-muted-foreground">
                                Completed {new Date(phase.completed_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {phase.completed && (
                            <Check className="h-5 w-5 text-green-600" strokeWidth={2} />
                          )}
                        </div>

                        {/* Tasks for this phase */}
                        <PhaseTasks
                          projectId={id}
                          phaseName={phase.name}
                          tasks={phase.tasks || []}
                          user={user}
                          onUpdate={fetchData}
                          assignees={assignees}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              )}
            </TabsContent>

            {/* Costs Tab */}
            <TabsContent value="costs">
              <LaborVsEstimate projectId={id} />
              <ProjectCosts projectId={id} />
            </TabsContent>

            {/* Files Tab (unified S3 storage) */}
            <TabsContent value="files">
              <LeadFiles projectId={id} />
            </TabsContent>

            {/* Estimates Tab */}
            <TabsContent value="estimates">
              <EstimateSection projectId={id} />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes">
              <ProjectNotes projectId={id} leadId={project.lead_id} />
            </TabsContent>

            {/* Change Orders Tab */}
            <TabsContent value="change-orders">
              <ProjectChangeOrders projectId={id} onChanged={fetchData} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Project Info */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Project Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {project.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-muted-foreground">Address</p>
                    <p className="text-sm">{project.address}{project.city && <>, {project.city}</>}{project.state && <>, {project.state}</>} {project.zip_code}</p>
                  </div>
                </div>
              )}
              {project.contract_value && (
                <div className="flex items-start gap-3">
                  <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-muted-foreground">Contract Value</p>
                    <p className="text-sm font-semibold text-green-700">{formatCurrency(project.contract_value)}</p>
                  </div>
                </div>
              )}
              {project.start_date && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-muted-foreground">Start Date</p>
                    <p className="text-sm">{new Date(project.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {project.estimated_end_date && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs text-muted-foreground">Est. Completion</p>
                    <p className="text-sm">{new Date(project.estimated_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                </div>
              )}
              {project.project_type && (
                <div>
                  <p className="text-xs text-muted-foreground">Project Type</p>
                  <Badge variant="outline" className="mt-1">{project.project_type}</Badge>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Project Size</p>
                <Select
                  value={project.project_size || 'standard'}
                  onValueChange={async (val) => {
                    try {
                      await projectApi.update(id, { project_size: val });
                      fetchData();
                      toast.success(val === 'small' ? 'Switched to simple task list' : 'Switched to full phase tracker');
                    } catch { toast.error('Failed to update'); }
                  }}
                >
                  <SelectTrigger className="h-8 w-full rounded-sm text-sm" data-testid="project-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (Phases)</SelectItem>
                    <SelectItem value="small">Small (Tasks Only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Billing Progress */}
          {billingSummary && (
            <Card className="rounded-sm" data-testid="billing-progress-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg">Billing Progress</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => navigate(`/invoices/new?client_id=${project.client_id || ''}&project_id=${id}`)}
                  data-testid="project-new-invoice-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Invoice
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {billingSummary.contract_value > 0 ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Invoiced</span>
                      <span className="font-semibold" data-testid="billing-invoiced">
                        {money(billingSummary.total_invoiced)} of {money(billingSummary.contract_value)}
                      </span>
                    </div>
                    <Progress value={Math.min(billingSummary.pct_invoiced, 100)} className="h-2" />
                    <div className="flex justify-between text-xs">
                      <span className="font-semibold text-blue-700" data-testid="billing-pct">
                        {billingSummary.pct_invoiced}% invoiced
                      </span>
                      <span className="text-muted-foreground" data-testid="billing-remaining">
                        {money(billingSummary.remaining)} remaining
                      </span>
                    </div>
                    {billingSummary.total_paid > 0 && (
                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span className="text-muted-foreground">Collected</span>
                        <span className="font-semibold text-green-700" data-testid="billing-paid">
                          {money(billingSummary.total_paid)}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Invoiced to date</span>
                      <span className="font-semibold" data-testid="billing-invoiced">{money(billingSummary.total_invoiced)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Set a contract value to track % invoiced.</p>
                  </>
                )}
                <p className="text-[11px] text-muted-foreground">
                  {billingSummary.invoice_count} invoice{billingSummary.invoice_count === 1 ? '' : 's'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Client Info */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="font-medium">{project.client_name}</p>
              {project.client_email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <a href={`mailto:${project.client_email}`} className="hover:text-orange-500">{project.client_email}</a>
                </div>
              )}
              {project.client_phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <a href={`tel:${project.client_phone}`} className="hover:text-orange-500">{project.client_phone}</a>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assigned Manager (Owner only) */}
          {isOwner && (
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="h-5 w-5" strokeWidth={1.5} />
                  Assigned Manager
                </CardTitle>
              </CardHeader>
              <CardContent>
                {managers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No managers in your team yet</p>
                ) : (
                  <Select 
                    value={project.assigned_manager_id || managers.find(m => m.role === 'owner')?.id} 
                    onValueChange={(val) => handleAssignManager(val)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="assign-manager-select">
                      <SelectValue placeholder="Select a manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {managers.map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {manager.name}{manager.role === 'owner' ? ' (Owner)' : ''}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {assignedManager && (
                  <div className="mt-3 p-2 bg-secondary/50 rounded-sm">
                    <p className="text-xs text-muted-foreground">Currently assigned to:</p>
                    <p className="font-medium text-sm">{assignedManager.name}</p>
                    {assignedManager.email && (
                      <p className="text-xs text-muted-foreground">{assignedManager.email}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {project.description && (
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{project.description}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Photo Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-3xl p-0">
          {selectedPhoto && (
            <div>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${selectedPhoto.image_url}`}
                alt={selectedPhoto.caption || 'Project photo'}
                className="w-full"
              />
              {(selectedPhoto.caption || selectedPhoto.phase) && (
                <div className="p-4">
                  {selectedPhoto.caption && <p className="font-medium">{selectedPhoto.caption}</p>}
                  {selectedPhoto.phase && <Badge variant="outline" className="mt-2">{selectedPhoto.phase}</Badge>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Project
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete <strong>{project?.name}</strong>? This action cannot be undone.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              All associated photos, documents, and progress data will be permanently removed.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-sm"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Project'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
